// components/steps/AuthStep.tsx
import React, { useState, useEffect } from 'react';
import { api } from '../../config/api.ts';
import { Client, Plan } from '../../types';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ModernInput } from '../ui/ModernInput.tsx';
import { ChevronLeftIcon, PhoneIcon, LockIcon, UserIcon } from '../icons/index.tsx';

interface AuthStepProps {
  onAuthenticated: (client: Client) => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  selectedPlan: Plan;
  selectedDeliverables: string[];
  setPaymentBreakdown: (breakdown: any) => void;
  step: number;
  setStep: (step: number) => void;
  loading?: boolean;
}

// Country configuration
const COUNTRIES = [
  { code: '+965', name: 'Kuwait', flag: '🇰🇼', maxLength: 8, placeholder: '12345678' },
  { code: '+1', name: 'USA', flag: '🇺🇸', maxLength: 10, placeholder: '2025551234' },
  { code: '+971', name: 'UAE', flag: '🇦🇪', maxLength: 9, placeholder: '501234567' },
  { code: '+44', name: 'UK', flag: '🇬🇧', maxLength: 10, placeholder: '7911123456' }
];

export const AuthStep: React.FC<AuthStepProps> = ({ 
  onAuthenticated, 
  onBack, 
  setError, 
  setLoading,
  selectedPlan,
  selectedDeliverables,
  setPaymentBreakdown,
  step,
  setStep,
  loading = false
}) => {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]); // Default to Kuwait
  const [phone, setPhone] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // Just the number part
  const [isExisting, setIsExisting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [autoLoggedIn, setAutoLoggedIn] = useState(false);
const [autoLoginChecked, setAutoLoginChecked] = useState(false);
  // Forgot password states
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('phone'); // phone, otp, password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetOtp, setResetOtp] = useState(''); // Store OTP for password reset
  
  // Add local loading state
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
const [client, setClient] = useState<Client | null>(null);

  // Auto-login check
useEffect(() => {
  checkExistingAuth();
}, []);

const checkExistingAuth = async () => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    try {
      setIsLoading(true);
      setLoading(true);
      
      // Set token in headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Get client info to verify token is still valid
      const response = await api.get('/client/info');      
      if (response.data.success) {
        // Store client but DON'T call calculateTaskCost yet
        setClient(response.data.data.client);
        setAutoLoggedIn(true);
        // REMOVED: await calculateTaskCost(response.data.data.client);
      }
    } catch (error) {
      // Token expired or invalid, remove it
      localStorage.removeItem('auth_token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setIsLoading(false);
      setLoading(false);
      setAutoLoginChecked(true);
    }
  } else {
    setAutoLoginChecked(true);
  }
};
  // Timer for resend cooldown
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

 const handlePhoneNumberChange = (value: string) => {
  const cleanNumber = value.replace(/\D/g, '');
  
  const maxLength = selectedCountry.maxLength;
  const finalNumber = cleanNumber.slice(0, maxLength);
  
  setPhoneNumber(finalNumber);
  setPhone(`${selectedCountry.code}${finalNumber}`);
};

  const handleCountrySelect = (country: typeof COUNTRIES[0]) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    // Reset phone number when country changes
    setPhoneNumber('');
    setPhone('');
  };

  const validatePhoneNumber = () => {
    if (selectedCountry.code === '+965' && phoneNumber.length !== 8) {
      setError('يرجى إدخال رقم هاتف كويتي صحيح مكون من 8 أرقام');
      return false;
    }
    if (selectedCountry.code === '+1') {
      if (phoneNumber.length !== 10) {
        setError('Please enter a valid US phone number with 10 digits');
        return false;
      }
      // Check if area code is valid (first digit must be 2-9)
      if (phoneNumber[0] === '0' || phoneNumber[0] === '1') {
        setError('Invalid US area code. Area codes cannot start with 0 or 1');
        return false;
      }
    }
    if (selectedCountry.code === '+971' && phoneNumber.length !== 9) {
      setError('يرجى إدخال رقم هاتف إماراتي صحيح مكون من 9 أرقام / Please enter a valid UAE phone number with 9 digits');
      return false;
    }
    if (selectedCountry.code === '+44' && phoneNumber.length !== 10) {
      setError('Please enter a valid UK phone number with 10 digits');
      return false;
    }
    return true;
  };

  const checkPhone = async () => {
    if (!validatePhoneNumber()) {
      return;
    }

    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/check-phone', { phone });
      if (response.data.success) {
        const data = response.data.data;
        setIsExisting(data.exists);
        if (!data.exists) {
          await sendOtp();
        }
      }
    } catch (err) {
      setError('فشل في التحقق من رقم الهاتف / Failed to verify phone number');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    try {
      const response = await api.post('/auth/send-otp', { phone });
      if (response.data.success) {
        setOtpSent(true);
        setResendCooldown(60); // 60 second cooldown before allowing resend
        setRemainingAttempts(3); // Reset attempts on new OTP
      }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('فشل في إرسال رمز التحقق / Failed to send verification code');
      }
    }
  };

  const resendOtp = async () => {
    try {
      setIsResending(true);
      const response = await api.post('/auth/resend-otp', { phone });
      if (response.data.success) {
        setResendCooldown(60); // Reset cooldown
        setOtp(''); // Clear the OTP input
        setRemainingAttempts(3); // Reset attempts
        // Show success message
        alert('تم إرسال رمز التحقق الجديد / New verification code sent');
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('الرجاء الانتظار قبل طلب رمز جديد / Please wait before requesting a new code');
      } else {
        setError('فشل في إعادة إرسال رمز التحقق / Failed to resend verification code');
      }
    } finally {
      setIsResending(false);
    }
  };

  const verifyOtp = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/verify-otp', { phone, otp });
      if (response.data.success) {
        setStep(5.5);
      }
    } catch (err: any) {
      if (err.response?.data?.data?.remaining_attempts !== undefined) {
        setRemainingAttempts(err.response.data.data.remaining_attempts);
        if (err.response.data.data.remaining_attempts === 0) {
          setError('تم تجاوز عدد المحاولات المسموح / Maximum attempts exceeded');
        } else {
          setError(`رمز التحقق غير صحيح. المحاولات المتبقية: ${err.response.data.data.remaining_attempts} / Invalid code. Remaining attempts: ${err.response.data.data.remaining_attempts}`);
        }
      } else {
        setError('رمز التحقق غير صحيح أو منتهي الصلاحية / Invalid or expired verification code');
      }
      setOtp(''); // Clear the OTP input on error
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const register = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/register', {
        phone,
        first_name: firstName,
        last_name: lastName,
        password,
        password_confirmation: password
      });
      if (response.data.success) {
        const token = response.data.data.token;
        localStorage.setItem('auth_token', token);
        
        // Set the token in axios headers immediately
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        await calculateTaskCost(response.data.data.client);
      }
    } catch (err) {
      setError('فشل التسجيل / Registration failed');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/login', { phone, password });
      if (response.data.success) {
        const token = response.data.data.token;
        localStorage.setItem('auth_token', token);
        
        // Set the token in axios headers immediately
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        await calculateTaskCost(response.data.data.client);
      }
    } catch (err) {
      setError('فشل تسجيل الدخول / Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const calculateTaskCost = async (client: Client) => {
    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/client/tasks/calculate-cost', {
        plan_id: selectedPlan.id,
        deliverables: selectedDeliverables
      });
      if (response.data.success) {
        const breakdown = response.data.data;
        setPaymentBreakdown(breakdown);
        onAuthenticated(client);
      }
    } catch (err) {
      setError('فشل في حساب تكلفة المهمة / Failed to calculate task cost');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Forgot Password Methods
  const initiateForgotPassword = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/forgot-password', { phone });
      if (response.data.success) {
        setForgotPasswordStep('otp');
        setResendCooldown(60);
        setRemainingAttempts(3);
        setOtp('');
      }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('فشل في إرسال رمز إعادة تعيين كلمة المرور / Failed to send password reset code');
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const verifyForgotPasswordOtp = async () => {
    if (otp.length !== 4) {
      setError('يرجى إدخال رمز التحقق المكون من 4 أرقام / Please enter the 4-digit verification code');
      return;
    }

    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/verify-otp', { phone, otp });
      if (response.data.success) {
        setResetOtp(otp); // Save the verified OTP
        setForgotPasswordStep('password');
      }
    } catch (err: any) {
      if (err.response?.data?.data?.remaining_attempts !== undefined) {
        setRemainingAttempts(err.response.data.data.remaining_attempts);
        if (err.response.data.data.remaining_attempts === 0) {
          setError('تم تجاوز عدد المحاولات المسموح / Maximum attempts exceeded');
        } else {
          setError(`رمز التحقق غير صحيح. المحاولات المتبقية: ${err.response.data.data.remaining_attempts} / Invalid code. Remaining attempts: ${err.response.data.data.remaining_attempts}`);
        }
      } else {
        setError('رمز التحقق غير صحيح أو منتهي الصلاحية / Invalid or expired verification code');
      }
      setOtp('');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('كلمات المرور غير متطابقة / Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل / Password must be at least 8 characters');
      return;
    }

    try {
      setIsLoading(true);
      setLoading(true);
      const response = await api.post('/auth/reset-password', {
        phone,
        otp: resetOtp, // Use the saved OTP instead of current otp state
        password: newPassword,
        password_confirmation: confirmPassword
      });
      
      if (response.data.success) {
        const token = response.data.data.token;
        localStorage.setItem('auth_token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Calculate task cost since this is for task purchase
        await calculateTaskCost(response.data.data.client);
      }
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const errors = Object.values(err.response.data.errors).flat();
        setError('فشل في إعادة تعيين كلمة المرور / Password reset failed: ' + errors.join(', '));
      } else {
        setError('فشل في إعادة تعيين كلمة المرور / Password reset failed');
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };
const handleLogout = () => {
  localStorage.removeItem('auth_token');
  delete api.defaults.headers.common['Authorization'];
  
  // Reset states
  setAutoLoggedIn(false);
  setClient(null);
  setPhone('');
  setPhoneNumber('');
  setPassword('');
  setIsExisting(false);
  setOtpSent(false);
  setOtp('');
};

const handleContinueWithAccount = async () => {
  if (client) {
    await calculateTaskCost(client);
  }
};
// Show loading while checking auto-login
if (!autoLoginChecked) {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">جاري التحقق...</p>
      </div>
    </div>
  );
}

// If auto-logged in, show account options
if (autoLoggedIn && client) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">التحقق من الهوية / Identity Verification</h2>
          <p className="text-gray-600">تم التعرف على حسابك / Account recognized</p>
        </div>
        <ModernButton 
          variant="outline" 
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع / Back
        </ModernButton>
      </div>

      <ModernCard className="p-6">
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold mb-2">مرحباً {client.first_name}!</h3>
            <p className="text-gray-600 mb-1">أنت مسجل الدخول بالفعل</p>
            <p className="text-gray-600 mb-6">الحساب: {client.phone}</p>
          </div>
          
          <div className="space-y-3">
            <ModernButton
              variant="primary"
              onClick={handleContinueWithAccount}
              className="w-full"
              loading={isLoading}
            >
              متابعة بهذا الحساب / Continue with this account
            </ModernButton>
            
            <ModernButton
              variant="outline"
              onClick={handleLogout}
              className="w-full"
            >
              استخدام حساب آخر / Use different account
            </ModernButton>
          </div>
        </div>
      </ModernCard>
    </div>
  );
}

// Normal auth flow (this is your existing return statement)
return (
  <div className="space-y-6">
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">التحقق من الهوية / Identity Verification</h2>
        <p className="text-gray-600">تحقق من حسابك للمتابعة / Verify your account to continue</p>
      </div>
      <ModernButton 
        variant="outline" 
        size="sm"
        onClick={onBack}
        icon={<ChevronLeftIcon />}
      >
        رجوع / Back
      </ModernButton>
    </div>

    <ModernCard className="p-6">
      {/* Step 1: Phone Number Input */}
      {!otpSent && !isExisting && !forgotPasswordMode && (

          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                رقم الهاتف / Phone Number
              </label>
              <div className="flex">
                {/* Country Selector */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className="flex items-center px-3 bg-gray-50 border border-gray-300 rounded-r-xl border-l-0 hover:bg-gray-100"
                  >
                    <span className="text-xl mr-2">{selectedCountry.flag}</span>
                    <PhoneIcon />
                    <span className="ml-2 text-gray-600 font-medium">{selectedCountry.code}</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Country Dropdown */}
                  {showCountryDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-[200px] max-h-[300px] overflow-y-auto">
                      {COUNTRIES.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className="flex items-center w-full px-4 py-3 hover:bg-blue-50 text-left border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <span className="text-xl mr-3">{country.flag}</span>
                          <span className="font-medium flex-1">{country.name}</span>
                          <span className="text-gray-500 text-sm">{country.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  placeholder={selectedCountry.placeholder}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-l-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-right"
                  maxLength={selectedCountry.maxLength}
                />
              </div>
              <p className="text-xs text-gray-500">
                {selectedCountry.code === '+965' && 'أدخل رقم الهاتف بدون +965'}
                {selectedCountry.code === '+1' && 'Enter phone number without +1'}
                {selectedCountry.code === '+971' && 'أدخل رقم الهاتف بدون 971+ / Enter number without +971'}
                {selectedCountry.code === '+44' && 'Enter phone number without +44'}
              </p>
            </div>
            
            <ModernButton
              variant="primary"
              onClick={checkPhone}
              className="w-full"
              disabled={phoneNumber.length < selectedCountry.maxLength}
              loading={isLoading}
            >
              متابعة / Continue
            </ModernButton>
          </div>
        )}

        {/* Step 2: Login for Existing Users */}
        {isExisting && !forgotPasswordMode && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">مرحباً بعودتك / Welcome Back</h3>
              <p className="text-gray-600">أدخل كلمة المرور / Enter password for {phone}</p>
            </div>
            
            <ModernInput
              label="كلمة المرور / Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="أدخل كلمة المرور / Enter password"
              icon={<LockIcon />}
            />
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => setForgotPasswordMode(true)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                نسيت كلمة المرور؟ / Forgot password?
              </button>
            </div>
            
            <ModernButton
              variant="primary"
              onClick={login}
              className="w-full"
              loading={isLoading}
              disabled={!password.trim()}
            >
              تسجيل الدخول / Login
            </ModernButton>
          </div>
        )}

        {/* Step 3: OTP Verification for New Users */}
        {otpSent && !isExisting && step === 5 && !forgotPasswordMode && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">التحقق من رقم الهاتف / Phone Verification</h3>
              <p className="text-gray-600">أدخل رمز التحقق المرسل إلى / Enter code sent to {phone}</p>
              {remainingAttempts < 3 && remainingAttempts > 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  المحاولات المتبقية / Remaining attempts: {remainingAttempts}
                </p>
              )}
            </div>
            
            <ModernInput
              label="رمز التحقق / Verification Code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="أدخل الرمز المكون من 4 أرقام / Enter 4-digit code"
              maxLength={4}
              type="tel"
            />
            
            <ModernButton
              variant="primary"
              onClick={verifyOtp}
              className="w-full"
              loading={isLoading}
              disabled={otp.length !== 4 || remainingAttempts === 0}
            >
              تحقق / Verify
            </ModernButton>
            
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-sm text-gray-500">
                  يمكنك إعادة الإرسال بعد / Resend available in {resendCooldown}s
                </p>
              ) : (
                <button 
                  onClick={resendOtp}
                  disabled={isResending}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  {isResending ? 'جاري الإرسال... / Sending...' : 'إعادة إرسال / Resend Code'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Registration */}
        {step === 5.5 && !forgotPasswordMode && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">إنشاء حساب / Create Account</h3>
              <p className="text-gray-600">أكمل التسجيل / Complete registration</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <ModernInput
                label="الاسم الأول / First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="الاسم الأول / First name"
                icon={<UserIcon />}
              />
              <ModernInput
                label="اسم العائلة / Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="اسم العائلة / Last name"
                icon={<UserIcon />}
              />
            </div>
            
            <ModernInput
              label="كلمة المرور / Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="كلمة مرور (8+ أحرف) / Password (8+ chars)"
              icon={<LockIcon />}
            />
            
            <ModernButton
              variant="primary"
              onClick={register}
              className="w-full"
              loading={isLoading}
              disabled={!firstName.trim() || !lastName.trim() || password.length < 8}
            >
              إنشاء الحساب / Create Account
            </ModernButton>
          </div>
        )}

        {/* Forgot Password - Phone Confirmation */}
        {forgotPasswordMode && forgotPasswordStep === 'phone' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">إعادة تعيين كلمة المرور / Reset Password</h3>
              <p className="text-gray-600">تأكيد رقم الهاتف / Confirm phone: {phone}</p>
            </div>
            
            <div className="flex gap-4">
              <ModernButton
                variant="primary"
                onClick={initiateForgotPassword}
                className="flex-1"
                loading={isLoading}
              >
                إرسال رمز إعادة التعيين / Send Reset Code
              </ModernButton>
              <ModernButton
                variant="outline"
                onClick={() => {
                  setForgotPasswordMode(false);
                  setForgotPasswordStep('phone');
                }}
                className="flex-1"
              >
                إلغاء / Cancel
              </ModernButton>
            </div>
          </div>
        )}

        {/* Forgot Password - OTP Verification */}
        {forgotPasswordMode && forgotPasswordStep === 'otp' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">التحقق من رمز إعادة التعيين / Verify Reset Code</h3>
              <p className="text-gray-600">أدخل الرمز المرسل إلى / Enter code sent to {phone}</p>
              {remainingAttempts < 3 && remainingAttempts > 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  المحاولات المتبقية / Remaining attempts: {remainingAttempts}
                </p>
              )}
            </div>
            
            <ModernInput
              label="رمز إعادة التعيين / Reset Code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="أدخل الرمز المكون من 4 أرقام / Enter 4-digit code"
              maxLength={4}
              type="tel"
            />
            
            <div className="flex gap-4">
              <ModernButton
                variant="primary"
                onClick={verifyForgotPasswordOtp}
                className="flex-1"
                loading={isLoading}
                disabled={otp.length !== 4 || remainingAttempts === 0}
              >
                تحقق / Verify
              </ModernButton>
              <ModernButton
                variant="outline"
                onClick={() => {
                  setForgotPasswordStep('phone');
                  setOtp('');
                }}
                className="flex-1"
              >
                رجوع / Back
              </ModernButton>
            </div>
            
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-sm text-gray-500">
                  يمكنك إعادة الإرسال بعد / Resend available in {resendCooldown}s
                </p>
              ) : (
                <button 
                  onClick={initiateForgotPassword}
                  disabled={isLoading}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  {isLoading ? 'جاري الإرسال... / Sending...' : 'إعادة إرسال / Resend Code'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Forgot Password - New Password */}
        {forgotPasswordMode && forgotPasswordStep === 'password' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">كلمة مرور جديدة / New Password</h3>
              <p className="text-gray-600">أدخل كلمة المرور الجديدة / Enter your new password</p>
            </div>
            
            <ModernInput
              label="كلمة المرور الجديدة / New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="أدخل كلمة مرور جديدة (8+ أحرف) / New password (8+ chars)"
              icon={<LockIcon />}
            />
            
            <ModernInput
              label="تأكيد كلمة المرور / Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="أعد إدخال كلمة المرور / Re-enter password"
              icon={<LockIcon />}
            />
            
            <div className="flex gap-4">
              <ModernButton
                variant="primary"
                onClick={resetPassword}
                className="flex-1"
                loading={isLoading}
                disabled={!newPassword.trim() || newPassword.length < 8 || newPassword !== confirmPassword}
              >
                تعيين كلمة المرور / Set Password
              </ModernButton>
              <ModernButton
                variant="outline"
                onClick={() => {
                  setForgotPasswordStep('otp');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="flex-1"
              >
                رجوع / Back
              </ModernButton>
            </div>
          </div>
        )}
      </ModernCard>
    </div>
  );
};